import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type { SxProps } from '@mui/material';
import type { Hexcode } from 'hephaestus/core';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useHelpTextTokens } from './helpText.tokens';

type HelpTextTone = 'success' | 'warning' | 'error' | 'neutral';
type HelpTextProps = {
  text?: string;
  tone?: HelpTextTone;
  showIcon?: boolean;
};

/**
 * Component to render help text in the UI when it is not possible to
 * show help text bound to individual inputs (e.g. reporting errors
 * for an entire form section or for inputs with insufficient space to
 * bind error messages, like Radio or Checkbox)
 */
export const HelpText = ({
  text = '',
  tone = 'neutral',
  showIcon = true,
}: HelpTextProps) => {
  const theme = useTheme();
  const tokens = useHelpTextTokens(theme);

  const getColor = (): Hexcode => {
    switch (tone) {
      case 'success':
        return tokens.successColor as Hexcode;
      case 'warning':
        return tokens.warningColor as Hexcode;
      case 'error':
        return tokens.errorColor as Hexcode;
      case 'neutral':
      default:
        return tokens.neutralColor as Hexcode;
    }
  };

  const iconSx: SxProps = { color: getColor(), mr: 0.5, fontSize: '0.875em' };
  const getIcon = (): JSX.Element => {
    switch (tone) {
      case 'success':
        return <CheckCircleIcon sx={iconSx} />;
      case 'warning':
        return <WarningAmberOutlinedIcon sx={iconSx} />;
      case 'error':
        return <ErrorIcon sx={iconSx} />;
      case 'neutral':
      default:
        return <InfoOutlinedIcon sx={iconSx} />;
    }
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        my: 1,
      }}
    >
      {showIcon && getIcon()}
      <Typography variant='bodySmall' sx={{ color: getColor() }}>
        {text}
      </Typography>
    </Box>
  );
};
